import { useState, useMemo } from 'react';
import { EnhancedStockData } from '@/types/deals';
import StockActionIcons from '../stocks/StockActionIcons';
import CompactCollapsibleSection from '@/components/ui/CompactCollapsibleSection';
import ClientLink from './ClientLink';
import { SortConfig, SortField, sortStocks, getMaxActivityDays } from '@/utils/sortUtils';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

interface EnhancedDealTableProps {
  stocks: EnhancedStockData[];
}

export default function EnhancedDealTable({ stocks }: EnhancedDealTableProps) {
  // Sorting state
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'market_capitalization',
    direction: 'desc'
  });
  
  // Global net position filter (applies to all stocks)
  const [netPositionFilter, setNetPositionFilter] = useState<number>(0); // Default 0% (no filter)

  // Apply sorting and filtering to stocks
  const filteredAndSortedStocks = useMemo(() => {
    // First filter stocks that have deals
    const stocksWithDeals = stocks.filter(stock => stock.deals && stock.deals.length > 0);
    
    // Then sort the stocks
    const sortedStocks = sortStocks(stocksWithDeals, sortConfig);
    
    // If no net position filter is applied, return all sorted stocks with deals
    if (netPositionFilter <= 0) return sortedStocks;
    
    // Filter stocks that have at least one client with net position above the threshold
    return sortedStocks.filter(stock => {
      if (!stock.clientDealSummaries || stock.clientDealSummaries.length === 0) return false;
      
      // Check if any client has a net position (buy or sell) above the threshold
      const totalShares = stock.financial_data?.number_of_equity_shares || 0;
      if (totalShares <= 0) return false;
      
      // Find if any client meets the filter criteria
      return stock.clientDealSummaries.some(clientSummary => {
        const netQuantity = clientSummary.totalPurchaseQuantity - clientSummary.totalSellQuantity;
        const netPositionPercentage = Math.abs(netQuantity / (totalShares * 10000000) * 100);
        return netPositionPercentage >= netPositionFilter;
      });
    });
  }, [stocks, sortConfig, netPositionFilter]);

  // Toggle sort direction or set new sort field
  const handleSort = (field: SortField) => {
    setSortConfig(prevConfig => {
      if (prevConfig.field === field) {
        // Toggle direction if same field
        return {
          ...prevConfig,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      } else {
        // New field, default to descending for most fields
        return {
          field,
          direction: field === 'stock_name' ? 'asc' : 'desc'
        };
      }
    });
  };

  // Helper function to render sort indicator
  const renderSortIndicator = (field: SortField) => {
    if (sortConfig.field !== field) return null;
    
    return sortConfig.direction === 'asc' 
      ? <ChevronUpIcon className="w-4 h-4 inline-block ml-1" /> 
      : <ChevronDownIcon className="w-4 h-4 inline-block ml-1" />;
  };

  // Helper function to format percentages with color coding
  const formatPercentage = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'N/A';
    
    const formattedValue = `${value.toFixed(2)}%`;
    const colorClass = value >= 0 
      ? 'text-emerald-700 dark:text-emerald-400 font-medium' 
      : 'text-rose-700 dark:text-rose-400 font-medium';
    
    return <span className={colorClass}>{formattedValue}</span>;
  };

  // Helper function to format currency
  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'N/A';
    
    // Store the sign
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const sign = isNegative ? '-' : '';
    
    // Format in lakhs and crores using absolute value
    if (absValue >= 10000000) { // ≥ 1 crore
      return `${sign}₹${(absValue / 10000000).toFixed(1)} Cr`;
    } else if (absValue >= 100000) { // ≥ 1 lakh
      return `${sign}₹${(absValue / 100000).toFixed(1)} L`;
    } else {
      return value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 2
      });
    }
  };

  // Helper function to format numbers
  const formatNumber = (value: number | null | undefined, decimals = 2) => {
    if (value === null || value === undefined) return 'N/A';
    
    // Store the sign
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const sign = isNegative ? '-' : '';
    
    // Format large numbers in lakhs and crores using absolute value
    if (absValue >= 10000000) { // ≥ 1 crore
      return `${sign}${(absValue / 10000000).toFixed(1)} Cr`;
    } else if (absValue >= 100000) { // ≥ 1 lakh
      return `${sign}${(absValue / 100000).toFixed(1)} L`;
    } else if (absValue >= 1000) { // ≥ 1 thousand
      return `${sign}${(absValue / 1000).toFixed(1)}K`;
    } else {
      return value.toLocaleString('en-IN', {
        maximumFractionDigits: decimals
      });
    }
  };

  // Handle global filter change
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setNetPositionFilter(isNaN(value) ? 0 : value);
  };

  // CSV Export function
  const downloadCSV = () => {
    const csvHeader = [
      'ISIN',
      'Stock Name',
      'NSE Code',
      'BSE Code',
      'Stock Code',
      'Industry',
      'Current Price',
      'Market Cap (Cr)',
      'P/E Ratio',
      'Total Deals',
      'TradingView Link',
      'Screener Link',
      'External Code'
    ].join(',');

    const csvRows = filteredAndSortedStocks.map(stock => {
      const row = [
        stock.isin,
        `"${stock.stock_name}"`,
        stock.nse_code || '',
        stock.bse_code || '',
        stock.stock_code || '',
        `"${stock.industry_name || ''}"`,
        stock.financial_data?.current_price ? stock.financial_data.current_price.toFixed(2) : 'N/A',
        stock.financial_data?.market_capitalization ? stock.financial_data.market_capitalization.toFixed(2) : 'N/A',
        stock.financial_data?.price_to_earning ? stock.financial_data.price_to_earning.toFixed(2) : 'N/A',
        stock.deals?.length || 0,
        stock.trading_view_navlink || '',
        stock.screener_navlink || '',
        stock.stock_code ? `"${stock.stock_code},"` : ''
      ].join(',');
      return row;
    });

    const csvContent = [csvHeader, ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `deal-stocks-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Check if there are any stocks with deals
  const stocksWithDeals = stocks.filter(stock => stock.deals && stock.deals.length > 0);
  
  if (stocksWithDeals.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="mt-2 text-lg font-medium text-zinc-900 dark:text-white">No Deals Found</h3>
        <p className="mt-1 text-zinc-500 dark:text-zinc-400">
          No deals found for the filtered stocks.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto space-y-3 overflow-visible">
      {/* Top-level filter control */}
      <div className="flex items-center justify-between gap-2 mb-2 p-2 rounded-lg border border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-800/60">
        <div className="flex items-center gap-2">
          <label htmlFor="global-net-position-filter" className="text-sm text-zinc-600 dark:text-zinc-300 whitespace-nowrap">
            Show stocks with clients having net position above:
          </label>
          <div className="flex items-center gap-1">
            <input
              id="global-net-position-filter"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={netPositionFilter}
              onChange={handleFilterChange}
              className="w-16 rounded-lg border border-zinc-300 bg-white px-2 py-1 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
            />
            <span className="text-sm text-zinc-600 dark:text-zinc-300">%</span>
          </div>
          
          <div className="ml-2 text-xs text-zinc-500 dark:text-zinc-400">
            {filteredAndSortedStocks.length} of {stocksWithDeals.length} stocks shown
          </div>
        </div>
        
        <button
          onClick={downloadCSV}
          className="inline-flex items-center rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800 transition-colors"
        >
          <ArrowDownTrayIcon className="w-4 h-4 mr-1.5" />
          Export CSV
        </button>
      </div>

      {/* Sorting controls */}
      <div className="flex flex-wrap items-center gap-2 mb-2 p-2 rounded-lg border border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-800/60">
        <span className="text-sm text-zinc-600 dark:text-zinc-300">Sort by:</span>
        
        <button 
          onClick={() => handleSort('stock_name')}
          className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
            sortConfig.field === 'stock_name' 
              ? 'border-transparent bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' 
              : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800/50'
          }`}
        >
          Name {renderSortIndicator('stock_name')}
        </button>
        
        <button 
          onClick={() => handleSort('current_price')}
          className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
            sortConfig.field === 'current_price' 
              ? 'border-transparent bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' 
              : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800/50'
          }`}
        >
          Price {renderSortIndicator('current_price')}
        </button>
        
        <button 
          onClick={() => handleSort('market_capitalization')}
          className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
            sortConfig.field === 'market_capitalization' 
              ? 'border-transparent bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' 
              : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800/50'
          }`}
        >
          Market Cap {renderSortIndicator('market_capitalization')}
        </button>
        
        <button 
          onClick={() => handleSort('price_to_earning')}
          className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
            sortConfig.field === 'price_to_earning' 
              ? 'border-transparent bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' 
              : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800/50'
          }`}
        >
          P/E Ratio {renderSortIndicator('price_to_earning')}
        </button>
        
        <button 
          onClick={() => handleSort('max_activity_days')}
          className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
            sortConfig.field === 'max_activity_days' 
              ? 'border-transparent bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' 
              : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800/50'
          }`}
        >
          Max Activity Days {renderSortIndicator('max_activity_days')}
        </button>
      </div>

      {filteredAndSortedStocks.map((stock) => {
        // Calculate max activity days for display
        const maxActivityDays = getMaxActivityDays(stock);
        
        return (
          <div 
            key={stock.isin} 
            className="relative rounded-2xl border border-zinc-200 bg-white shadow-sm transition-shadow hover:shadow-md hover:z-20 dark:border-zinc-800 dark:bg-zinc-900"
            id={`stock-${stock.isin}`}
          >
            {/* Redesigned Stock Header with better information hierarchy and reduced vertical space */}
            <div className="rounded-t-2xl border-b border-zinc-200 bg-zinc-50 p-3 sticky top-16 z-30 dark:border-zinc-800 dark:bg-zinc-800">
              <div className="flex flex-wrap justify-between items-center">
                {/* Left section - Stock name and badges */}
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex-1 min-w-0">
                    <StockActionIcons 
                      isin={stock.isin}
                      stockName={stock.stock_name}
                      tradingViewLink={stock.trading_view_navlink}
                      screenerLink={stock.screener_navlink}
                      currentPrice={stock.financial_data?.current_price}
                      showNotesCount
                    />
                    <h3 className="text-base font-semibold text-zinc-900 dark:text-white truncate">{stock.stock_name}</h3>
                    {stock.financial_data?.is_sme && 
                      <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-100 whitespace-nowrap">
                        SME
                      </span>
                    }
                  </div>
                </div>
                
                {/* Right section - Price and returns */}
                <div className="flex items-center gap-3">
                  {/* Returns as badges */}
                  <div className="flex items-center gap-1.5">
                    <div className="px-1.5 py-0.5 rounded border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                      <span className="text-xs text-zinc-600 dark:text-zinc-400">1D:</span>
                      <span className="text-xs font-medium ml-0.5">{formatPercentage(stock.financial_data?.return_over_1day)}</span>
                    </div>
                    <div className="px-1.5 py-0.5 rounded border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                      <span className="text-xs text-zinc-600 dark:text-zinc-400">1W:</span>
                      <span className="text-xs font-medium ml-0.5">{formatPercentage(stock.financial_data?.return_over_1week)}</span>
                    </div>
                    <div className="px-1.5 py-0.5 rounded border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                      <span className="text-xs text-zinc-600 dark:text-zinc-400">1M:</span>
                      <span className="text-xs font-medium ml-0.5">{formatPercentage(stock.financial_data?.return_over_1month)}</span>
                    </div>
                    <div className="px-1.5 py-0.5 rounded border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                      <span className="text-xs text-zinc-600 dark:text-zinc-400">3M:</span>
                      <span className="text-xs font-medium ml-0.5">{formatPercentage(stock.financial_data?.return_over_3months)}</span>
                    </div>
                  </div>
                  
                  {/* Current Price */}
                  <div className="flex flex-col items-end">
                    <div className="text-base font-semibold text-zinc-900 dark:text-white">
                      {formatCurrency(stock.financial_data?.current_price)}
                    </div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">
                      Market Cap: {formatNumber(stock.financial_data?.market_capitalization)} Cr
                    </div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">
                      Free Float Cap: {formatNumber(stock.financial_data?.free_float_mcap)} Cr
                    </div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">
                      P/E Ratio: {formatNumber(stock.financial_data?.price_to_earning)}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Stock metadata in a clean horizontal layout with badges */}
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                <div className="px-2 py-0.5 rounded border border-zinc-200 bg-white text-xs dark:border-zinc-700 dark:bg-zinc-900">
                  <span className="text-zinc-500 dark:text-zinc-400">Industry:</span>
                  <span className="ml-1 text-zinc-900 dark:text-white">{stock.industry_name}</span>
                </div>
                
                <div className="px-2 py-0.5 rounded border border-zinc-200 bg-white text-xs dark:border-zinc-700 dark:bg-zinc-900">
                  <span className="text-zinc-500 dark:text-zinc-400">Promoter:</span>
                  <span className="ml-1 text-zinc-900 dark:text-white">{stock.financial_data?.promoter_holding ? `${stock.financial_data.promoter_holding.toFixed(2)}%` : 'N/A'}</span>
                </div>
                
                <div className="px-2 py-0.5 rounded border border-zinc-200 bg-white text-xs dark:border-zinc-700 dark:bg-zinc-900">
                  <span className="text-zinc-500 dark:text-zinc-400">Total Shares:</span>
                  <span className="ml-1 text-zinc-900 dark:text-white">{formatNumber(stock.financial_data?.number_of_equity_shares, 2)} Cr</span>
                </div>
                
                <div className="px-2 py-0.5 rounded border border-zinc-200 bg-white text-xs dark:border-zinc-700 dark:bg-zinc-900">
                  <span className="text-zinc-500 dark:text-zinc-400">Free Float Shares:</span>
                  <span className="ml-1 text-zinc-900 dark:text-white">{formatNumber(stock.financial_data?.free_float_shares,2)} Cr</span>
                  {stock.financial_data?.free_float_shares && stock.financial_data?.number_of_equity_shares && 
                    <span className="ml-1 text-zinc-900 dark:text-white">
                      ({formatNumber((stock.financial_data.free_float_shares / stock.financial_data.number_of_equity_shares * 100) || 0, 0)}%)
                    </span>
                  }
                </div>
                
                {/* Max Activity Days badge */}
                <div className="px-2 py-0.5 bg-emerald-500/10 rounded text-xs">
                  <span className="text-emerald-700 dark:text-emerald-400">Max Activity Days:</span>
                  <span className="ml-1 font-semibold text-emerald-700 dark:text-emerald-400">{maxActivityDays}</span>
                </div>
                
                {/* Surveillance badges */}
                {stock.surveillances && stock.surveillances.length > 0 && (
                  stock.surveillances.map((surveillance, idx) => (
                    <div key={idx} className="px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800/30">
                      {surveillance.surveillance_type} {surveillance.stage}
                    </div>
                  ))
                )}
                
                {/* Special events badges */}
                {stock.listings && stock.listings.length > 0 && (
                  stock.listings.map((listing, idx) => (
                    <div key={idx} className="px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800/30">
                      {listing.listing_type}
                      {listing.rights && ` (${listing.rights})`}
                      {listing.status && ` - ${listing.status}`}
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* Stock Performance - Simplified layout */}
            <div className="p-3 rounded-b-2xl bg-white dark:bg-zinc-900">
              {/* Client Deal Summary - Reorganized layout */}
              {stock.clientDealSummaries && stock.clientDealSummaries.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-zinc-800 dark:text-white mb-1 flex items-center bg-zinc-50 dark:bg-zinc-800/60 px-2 py-1 rounded-md">
                    <svg className="w-3 h-3 mr-1 text-zinc-600 dark:text-zinc-300" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Client Deal Summary
                  </h4>
                  
                  <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-800">
                    <table className="min-w-full divide-y divide-zinc-200 dark:divide-zinc-800">
                      <thead className="sticky top-0 z-[1]">
                        <tr>
                          <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider bg-zinc-50 dark:bg-zinc-800">Client Name</th>
                          <th className="px-2 py-1 text-right text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider bg-zinc-50 dark:bg-zinc-800">Purchase Qty</th>
                          <th className="px-2 py-1 text-right text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider bg-zinc-50 dark:bg-zinc-800">Sell Qty</th>
                          <th className="px-2 py-1 text-right text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider bg-zinc-50 dark:bg-zinc-800">Purchase Value</th>
                          <th className="px-2 py-1 text-right text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider bg-zinc-50 dark:bg-zinc-800">Sell Value</th>
                          <th className="px-2 py-1 text-right text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider bg-zinc-50 dark:bg-zinc-800">Net Buy/Sell</th>
                          <th className="px-2 py-1 text-center text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider bg-zinc-50 dark:bg-zinc-800">Buy Days</th>
                          <th className="px-2 py-1 text-center text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider bg-zinc-50 dark:bg-zinc-800">Sell Days</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
                        {stock.clientDealSummaries.map((clientSummary, index) => {
                          // Calculate buy and sell days
                          const buyDays = new Set();
                          const sellDays = new Set();
                          
                          // Assuming we have access to the original deals to count days
                          stock.deals?.forEach(deal => {
                            if (deal.client_name === clientSummary.clientName) {
                              const dealDate = new Date(deal.reported_date).toISOString().split('T')[0];
                              if (deal.action === 'Purchase') {
                                buyDays.add(dealDate);
                              } else if (deal.action === 'Sell') {
                                sellDays.add(dealDate);
                              }
                            }
                          });
                          
                          // Calculate net position
                          const netQuantity = clientSummary.totalPurchaseQuantity - clientSummary.totalSellQuantity;
                          
                          // Calculate percentage of company
                          const totalShares = stock.financial_data?.number_of_equity_shares || 0;
                          const netPositionPercentage = totalShares > 0 ? (netQuantity / (totalShares * 10000000)) * 100 : 0;
                          
                          // Determine text color based on net position
                          const netPositionColorClass = netQuantity >= 0 
                            ? 'text-emerald-700 dark:text-emerald-400 font-medium' 
                            : 'text-rose-700 dark:text-rose-400 font-medium';
                          
                          // Highlight rows that meet the filter criteria
                          const meetsFilterCriteria = Math.abs(netPositionPercentage) >= netPositionFilter;
                          const rowHighlight = meetsFilterCriteria && netPositionFilter > 0 
                            ? 'bg-yellow-50 dark:bg-yellow-900/20' 
                            : 'bg-white dark:bg-zinc-900';
                          
                          return (
                            <tr key={index} className={`${rowHighlight} hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors duration-150`}>
                              <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-900 dark:text-zinc-200 font-medium">
                                <div className="flex items-center space-x-1">
                                  <span>{clientSummary.clientName}</span>
                                  <ClientLink clientName={clientSummary.clientName} />
                                </div>
                              </td>
                              <td className="px-2 py-1 whitespace-nowrap text-xs text-right text-emerald-700 dark:text-emerald-400 font-medium">
                                {clientSummary.totalPurchaseQuantity > 0 ? formatNumber(clientSummary.totalPurchaseQuantity, 0) : '-'}
                              </td>
                              <td className="px-2 py-1 whitespace-nowrap text-xs text-right text-rose-700 dark:text-rose-400 font-medium">
                                {clientSummary.totalSellQuantity > 0 ? formatNumber(clientSummary.totalSellQuantity, 0) : '-'}
                              </td>
                              <td className="px-2 py-1 whitespace-nowrap text-xs text-right text-emerald-700 dark:text-emerald-400">
                                {clientSummary.totalPurchaseValue > 0 ? formatCurrency(clientSummary.totalPurchaseValue) : '-'}
                              </td>
                              <td className="px-2 py-1 whitespace-nowrap text-xs text-right text-rose-700 dark:text-rose-400">
                                {clientSummary.totalSellValue > 0 ? formatCurrency(clientSummary.totalSellValue) : '-'}
                              </td>
                              <td className="px-2 py-1 whitespace-nowrap text-xs text-right">
                                <span className={netPositionColorClass}>
                                  {formatNumber(netQuantity, 0)} ({netPositionPercentage.toFixed(2)}%)
                                </span>
                              </td>
                              <td className="px-2 py-1 whitespace-nowrap text-xs text-center text-emerald-700 dark:text-emerald-400 font-medium">
                                {buyDays.size > 0 ? buyDays.size : '-'}
                              </td>
                              <td className="px-2 py-1 whitespace-nowrap text-xs text-center text-rose-700 dark:text-rose-400 font-medium">
                                {sellDays.size > 0 ? sellDays.size : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Recent Deals - Compact Collapsible with minimal spacing */}
              {stock.deals && stock.deals.length > 0 && (
                <div className="mt-2">
                  <CompactCollapsibleSection
                    title={`Recent Deals (${stock.deals.length})`}
                    defaultOpen={false} 
                  >
                    <div className="rounded-lg border border-zinc-200 dark:border-zinc-800">
                      <div className="max-h-64 overflow-y-auto">
                        <table className="min-w-full divide-y divide-zinc-200 dark:divide-zinc-800">
                          <thead className="sticky top-0 z-[1]">
                            <tr>
                              <th className="px-2 py-0.5 text-left text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider bg-zinc-50 dark:bg-zinc-800">Date</th>
                              <th className="px-2 py-0.5 text-left text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider bg-zinc-50 dark:bg-zinc-800">Client</th>
                              <th className="px-2 py-0.5 text-left text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider bg-zinc-50 dark:bg-zinc-800">Type</th>
                              <th className="px-2 py-0.5 text-left text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider bg-zinc-50 dark:bg-zinc-800">Action</th>
                              <th className="px-2 py-0.5 text-right text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider bg-zinc-50 dark:bg-zinc-800">Price</th>
                              <th className="px-2 py-0.5 text-right text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider bg-zinc-50 dark:bg-zinc-800">Quantity</th>
                              <th className="px-2 py-0.5 text-right text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider bg-zinc-50 dark:bg-zinc-800">Value</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
                            {stock.deals.map((deal, index) => {
                              return (
                                <tr key={index} className="bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors duration-150">
                                  <td className="px-2 py-0.5 whitespace-nowrap text-xs text-zinc-900 dark:text-zinc-200">
                                    {new Date(deal.reported_date).toLocaleDateString()}
                                  </td>
                                  <td className="px-2 py-0.5 whitespace-nowrap text-xs text-zinc-900 dark:text-zinc-200">
                                    <div className="flex items-center space-x-1">
                                      <span>{deal.client_name}</span>
                                      <ClientLink clientName={deal.client_name} />
                                    </div>
                                  </td>
                                  <td className="px-2 py-0.5 whitespace-nowrap text-xs text-zinc-900 dark:text-zinc-200">
                                    {deal.deal_type}
                                  </td>
                                  <td className="px-2 py-0.5 whitespace-nowrap text-xs text-zinc-900 dark:text-zinc-200">
                                    <span className={`px-1 py-0.5 text-xs rounded-full ${
                                      deal.action === 'Purchase' 
                                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300' 
                                        : 'bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-300'
                                    }`}>
                                      {deal.action}
                                    </span>
                                  </td>
                                  <td className="px-2 py-0.5 whitespace-nowrap text-xs text-right text-zinc-900 dark:text-zinc-200">
                                    {deal.average_price ? formatCurrency(deal.average_price) : 'N/A'}
                                  </td>
                                  <td className="px-2 py-0.5 whitespace-nowrap text-xs text-right text-zinc-900 dark:text-zinc-200">
                                    {formatNumber(deal.quantity, 0)}
                                  </td>
                                  <td className="px-2 py-0.5 whitespace-nowrap text-xs text-right text-zinc-900 dark:text-zinc-200">
                                    {deal.average_price ? formatCurrency(deal.average_price * deal.quantity) : 'N/A'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </CompactCollapsibleSection>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
} 